import React from 'react';
import { Tag, Tooltip, theme } from 'antd';
import { Link } from 'react-router-dom';
import { Muted } from './healthMonitorCells';
import {
  bySeverity,
  countStates,
  CHIP_STYLE,
  HealthServer,
  MAX_NETWORK_CHIPS,
  MAX_PROXY_CHIPS,
  NetworkStatus,
  ProxyStatus,
  STATUS_COLORS,
} from './healthMonitorTypes';

// The status pills are the point of this screen, so they keep their colour and
// their text — what is bounded is how many of them a single row may spend
// height on. The worst states are always the ones rendered; the tail collapses
// into a "+N" chip whose tooltip carries the breakdown.

const Strip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minWidth: 0 }}>{children}</div>
);

// A chip reads "<identity> <STATE>". When it has to be clipped, the identity
// gives way: the state word sits in its own non-shrinking span, so the
// ellipsis never eats the one word the operator is scanning for (it used to
// render "192.0.2.30 SUSPE…").
const StatusChip: React.FC<{ state: string; clickable?: boolean; children: React.ReactNode }> = ({
  state, clickable, children,
}) => (
  <Tag
    color={STATUS_COLORS[state] || 'default'}
    style={{
      ...CHIP_STYLE,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      cursor: clickable ? 'pointer' : undefined,
    }}
  >
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    {state !== 'HEALTHY' && <span style={{ flex: '0 0 auto', fontWeight: 600 }}>{state}</span>}
  </Tag>
);

const OverflowChip: React.FC<{ serverKey: string; hiddenStates: string[]; noun: string }> = ({
  serverKey, hiddenStates, noun,
}) => {
  const counts = countStates(hiddenStates);
  const tooltip = (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{hiddenStates.length} more {noun}</div>
      {Object.entries(counts).map(([state, n]) => (
        <div key={state} style={{ fontSize: 12 }}>{n} × {state}</div>
      ))}
      <div style={{ fontSize: 12, marginTop: 4 }}>Open Details for the full list.</div>
    </div>
  );
  return (
    <Tooltip title={tooltip}>
      <Link to={`/servers/${serverKey}/health`}>
        <Tag style={{ ...CHIP_STYLE, cursor: 'pointer' }}>+{hiddenStates.length}</Tag>
      </Link>
    </Tooltip>
  );
};

export const NetworkChips: React.FC<{ record: HealthServer }> = ({ record }) => {
  // Mobile-gateway servers don't use secondary networks — modems are in the Proxies column.
  if (record.type === 'mobile-gateway') return <Muted>--</Muted>;
  const nets = record.networkStatuses || [];
  if (nets.length === 0) return <Muted>--</Muted>;
  const sorted = bySeverity<NetworkStatus>(nets, (n) => n.state);
  const visible = sorted.slice(0, MAX_NETWORK_CHIPS);
  const hidden = sorted.slice(MAX_NETWORK_CHIPS);
  return (
    <Strip>
      {visible.map((n) => (
        <Tooltip key={n.ip} title={`${n.ip} — ${n.state}${n.staleSince ? ` (stale since ${n.staleSince})` : ''}`}>
          <StatusChip state={n.state}>{n.ip}</StatusChip>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <OverflowChip
          serverKey={record.key}
          hiddenStates={hidden.map((n) => n.state)}
          noun="networks"
        />
      )}
    </Strip>
  );
};

const stepLabel = (name: string): string => {
  const labels: Record<string, string> = {
    proxy_connect: 'Connect',
    proxy_request: 'Request',
    proxy_latency: 'Tunnel',
    proxy_egress_geo: 'Egress',
  };
  return labels[name] || name;
};

const ProxyTooltip: React.FC<{ p: ProxyStatus; isMobile: boolean; modemNumber: number }> = ({
  p, isMobile, modemNumber,
}) => {
  const { token } = theme.useToken();
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {isMobile ? `Modem #${modemNumber}` : (p.protocol || '').toUpperCase()}
        {p.label ? ` — ${p.label}` : ''} — {p.host}:{p.port} — {p.state}
      </div>
      {isMobile && p.country && (
        <div style={{ fontSize: 12, marginBottom: 2 }}>Egress country: {p.country}</div>
      )}
      {(p.steps || []).map((s) => {
        const icon = !s.success ? '✗' : s.severity === 'warn' ? '⚠' : s.severity === 'info' ? 'ℹ' : '✓';
        const iconColor = !s.success ? token.colorError : s.severity === 'warn' ? token.colorWarning : s.severity === 'info' ? token.colorInfo : token.colorSuccess;
        return (
          <div key={s.name} style={{ fontSize: 12 }}>
            <span style={{ color: iconColor, marginRight: 4 }}>{icon}</span>
            {stepLabel(s.name)}: {s.latency > 0 ? `${s.latency.toFixed(0)}ms` : '--'}
            {s.error && <span style={{ color: token.colorError }}> ({s.error})</span>}
            {Array.isArray(s.notes) && s.notes.length > 0 && (
              <div style={{ color: iconColor, paddingLeft: 14, opacity: 0.85 }}>{s.notes.join(' · ')}</div>
            )}
          </div>
        );
      })}
      {p.totalDuration > 0 && (
        <div style={{ fontSize: 12, marginTop: 2, borderTop: `1px solid ${token.colorSplit}`, paddingTop: 2 }}>
          Total: {p.totalDuration.toFixed(0)}ms
        </div>
      )}
    </div>
  );
};

export const ProxyChips: React.FC<{ record: HealthServer }> = ({ record }) => {
  const proxies = record.proxyStatuses || [];
  if (proxies.length === 0) return <Muted>--</Muted>;
  const isMobile = record.type === 'mobile-gateway';

  // Sort by state severity first so the worst-off modems surface in the
  // visible slice — a mobile gateway can carry 30+ of them and stacking them
  // all turns one row into a screenful.
  const withIdx = proxies.map((p: ProxyStatus, originalIndex: number) => ({ p, originalIndex }));
  const sorted = bySeverity(withIdx, (entry) => entry.p.state);
  const visible = sorted.slice(0, MAX_PROXY_CHIPS);
  const hidden = sorted.slice(MAX_PROXY_CHIPS);

  return (
    <Strip>
      {visible.map(({ p, originalIndex }) => {
        // A proxy on the server's own IP needs no host on the chip — the row
        // already shows it — so only the port is printed, ":8080". Any other
        // host is printed in full: abbreviating it destroyed the address
        // (three proxies on 198.51.100.42/43/44 all rendered the same).
        const chipAddr = p.host === record.mainIP ? `:${p.port}` : `${p.host}:${p.port}`;
        return (
          <Tooltip
            key={`${p.host}:${p.port}`}
            title={<ProxyTooltip p={p} isMobile={isMobile} modemNumber={originalIndex + 1} />}
          >
            <StatusChip state={p.state} clickable>
              {isMobile && p.country && (
                <img
                  src={`https://flagcdn.com/16x12/${p.country.toLowerCase()}.png`}
                  alt={p.country}
                  width={12}
                  height={9}
                  style={{ verticalAlign: 'baseline', marginRight: 3 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              {isMobile
                ? `#${originalIndex + 1}${p.label ? ' ' + p.label : ` ${chipAddr}`}`
                : (p.label || `${(p.protocol || '').toUpperCase()} ${chipAddr}`)}
            </StatusChip>
          </Tooltip>
        );
      })}
      {hidden.length > 0 && (
        <OverflowChip
          serverKey={record.key}
          hiddenStates={hidden.map(({ p }) => p.state)}
          noun={isMobile ? 'modems' : 'proxies'}
        />
      )}
    </Strip>
  );
};
